import { ArrowRight, TrendingUp, Droplets, Flame, Mountain, Sun, Wind, Leaf, Globe2, Zap, Atom, Gem, CloudCog, DollarSign, Building2, Users, Shield, LineChart, BarChart3, Target, Factory, TrendingDown } from 'lucide-react';
import { Link } from 'react-router-dom';

const Commodities = () => {
  // Updated commodities data with 12 categories in specified order
  const commodities = [
    {
      name: 'Commodity Prices',
      description: 'Real-time price intelligence across major energy and mineral commodities with historical trends and forecasting models.',
      image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80',
      coverage: ['Crude Oil', 'Natural Gas', 'Coal', 'Critical Minerals', 'Carbon Credits']
    },
    {
      name: 'Crude Oil',
      description: 'Comprehensive crude oil market analysis covering global supply-demand dynamics, refinery operations, and trade flows.',
      image: 'https://images.unsplash.com/photo-1570065843263-db1cc228c6e4?w=800&q=80',
      coverage: ['Prices', 'Reserves', 'Production', 'Consumption', 'Trade Movements', 'Refinery Capacity and Production']
    },
    {
      name: 'Natural Gas',
      description: 'In-depth natural gas intelligence including LNG markets, pipeline infrastructure, and emerging hydrogen production.',
      image: 'https://images.unsplash.com/photo-1761848021920-ff2a212671be?w=800&q=80',
      coverage: ['Prices', 'Reserves', 'Production', 'Consumption', 'Hydrogen Production Capacity', 'Trade Movements', 'Liquefaction Capacity and Production']
    },
    {
      name: 'Coal',
      description: 'Strategic coal market analysis covering thermal and metallurgical coal across major producing and consuming regions.',
      image: 'https://images.unsplash.com/photo-1632734825563-e180215f0c90?w=800&q=80',
      coverage: ['Prices', 'Reserves', 'Production', 'Consumption', 'Trade Movements']
    },
    {
      name: 'Solar Energy',
      description: 'Comprehensive solar energy intelligence tracking photovoltaic installations, capacity growth, and technology developments.',
      image: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=800&q=80',
      coverage: ['Installed Capacity', 'Installation by Type']
    },
    {
      name: 'Wind Energy',
      description: 'Wind power market analysis covering onshore and offshore installations, turbine technologies, and capacity expansion.',
      image: 'https://images.unsplash.com/photo-1591511017724-d2e18d65bf5d?w=800&q=80',
      coverage: ['Installed Capacity', 'Installation by Type']
    },
    {
      name: 'Bio Energy',
      description: 'Bioenergy sector intelligence covering biomass, biofuels, and organic feedstock markets across global regions.',
      image: 'https://images.unsplash.com/photo-1759302081336-f79d96ecf83f?w=800&q=80',
      coverage: ['Production', 'Consumption']
    },
    {
      name: 'Geothermal Energy',
      description: 'Geothermal power analysis tracking steam field developments, plant operations, and capacity expansion globally.',
      image: 'https://images.unsplash.com/photo-1759934553802-749f348a50b3?w=800&q=80',
      coverage: ['Production']
    },
    {
      name: 'Hydropower',
      description: 'Hydroelectric power intelligence covering dam operations, water resource management, and capacity developments.',
      image: 'https://images.unsplash.com/photo-1575041057420-505200c5f8d0?w=800&q=80',
      coverage: ['Production']
    },
    {
      name: 'Nuclear Energy',
      description: 'Nuclear power market analysis covering reactor operations, uranium markets, and global capacity planning.',
      image: 'https://images.unsplash.com/photo-1700607101915-99650c815caf?w=800&q=80',
      coverage: ['Production']
    },
    {
      name: 'Critical Minerals',
      description: 'Strategic intelligence on lithium, cobalt, rare earth elements, and other critical minerals essential for energy transition.',
      image: 'https://images.unsplash.com/photo-1634588513592-e34817f2e9e5?w=800&q=80',
      coverage: ['Prices', 'Reserves', 'Production', 'Consumption', 'Trade Movements']
    },
    {
      name: 'Emissions Tracking',
      description: 'Comprehensive emissions monitoring and analysis covering greenhouse gas data, carbon markets, and climate policy impacts.',
      image: 'https://images.unsplash.com/photo-1769445205155-5158d692d3ed?w=800&q=80',
      coverage: ['Global GHG Emissions', 'Sectoral Emissions', 'Historical Emissions', 'Forward Emissions', 'Regional Emissions'],
      anchor: 'emissions-section'
    }
  ];

  // Emissions Intelligence Data
  const emissionsServices = [
    {
      icon: <LineChart className="w-8 h-8 text-[#007BFF]" />,
      title: 'Emissions Monitoring',
      description: 'Real-time tracking and analysis of carbon emissions across global energy operations and supply chains'
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-[#007BFF]" />,
      title: 'Carbon Markets Intelligence',
      description: 'Comprehensive coverage of carbon pricing mechanisms, trading volumes, and regulatory developments'
    },
    {
      icon: <Target className="w-8 h-8 text-[#007BFF]" />,
      title: 'Net-Zero Pathways',
      description: 'Strategic analysis of decarbonization scenarios, technology adoption, and transition economics'
    },
    {
      icon: <Factory className="w-8 h-8 text-[#007BFF]" />,
      title: 'Sector Analysis',
      description: 'Detailed emissions profiles by energy sector including oil & gas, power generation, and industrial facilities'
    }
  ];

  const trackingCapabilities = [
    {
      title: 'Scope 1, 2 & 3 Emissions',
      description: 'Comprehensive tracking across direct emissions, purchased energy, and value chain activities'
    },
    {
      title: 'Carbon Intensity Metrics',
      description: 'Benchmarking and analysis of emissions per unit of energy production across technologies and regions'
    },
    {
      title: 'Regulatory Compliance',
      description: 'Monitoring of emissions reporting requirements, carbon border adjustments, and climate policy mandates'
    },
    {
      title: 'Abatement Technologies',
      description: 'Assessment of carbon capture, hydrogen, and other emissions reduction technologies and their economics'
    }
  ];

  const carbonMarkets = [
    {
      name: 'EU ETS',
      description: 'European Union Emissions Trading System',
      coverage: ['Carbon allowances', 'Compliance markets', 'Price forecasts']
    },
    {
      name: 'US Regional Markets',
      description: 'RGGI, California Cap-and-Trade, and emerging programs',
      coverage: ['State policies', 'Trading volumes', 'Price dynamics']
    },
    {
      name: 'Voluntary Carbon Markets',
      description: 'Project-based offsets and corporate commitments',
      coverage: ['Credit pricing', 'Project types', 'Quality standards']
    },
    {
      name: 'Global Carbon Pricing',
      description: 'International mechanisms and bilateral agreements',
      coverage: ['Carbon taxes', 'Border adjustments', 'Policy trends']
    }
  ];

  // Client types
  const clientTypes = [
    { icon: <Droplets className="w-6 h-6" />, name: 'Oil & Gas Companies' },
    { icon: <Sun className="w-6 h-6" />, name: 'Renewable Energy Developers' },
    { icon: <Mountain className="w-6 h-6" />, name: 'Mining Companies' },
    { icon: <TrendingUp className="w-6 h-6" />, name: 'Commodity Traders' },
    { icon: <Building2 className="w-6 h-6" />, name: 'Banks & Financial Institutions' },
    { icon: <DollarSign className="w-6 h-6" />, name: 'Private Equity Investors' },
    { icon: <Shield className="w-6 h-6" />, name: 'Governments' },
    { icon: <Users className="w-6 h-6" />, name: 'Regulators' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 whitespace-nowrap">
              Our Product Portfolio
            </h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              Intelligence platforms delivering proprietary data, market analysis, and strategic insights across global energy and natural resources markets.
            </p>
          </div>
        </div>
      </section>

      {/* Intelligence Platforms Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-4">Our Intelligence Platforms</h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-4xl mx-auto leading-relaxed">
              Energia IQ is a suite of integrated intelligence platforms designed to provide decision-makers with deep insights into global energy markets. Each platform combines proprietary data, expert research, and advanced modelling tools to help organizations evaluate markets, assess investment opportunities, and navigate the rapidly evolving global energy system.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {commodities.map((commodity, index) => (
              <div
                key={index}
                id={commodity.anchor || ''}
                className="bg-slate-50 rounded-xl border border-slate-200 hover:border-[#007BFF] hover:shadow-xl transition-all duration-300 overflow-hidden group flex flex-col"
              >
                {/* Image Section with White Title */}
                <div className="relative h-48 overflow-hidden flex-shrink-0">
                  <img
                    src={commodity.image}
                    alt={commodity.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A4D]/80 to-transparent"></div>
                  <h3 className="absolute bottom-4 left-4 text-2xl font-bold text-white">
                    {commodity.name}
                  </h3>
                </div>

                {/* Description */}
                <div className="p-6 border-b border-slate-200 bg-white">
                  <p className="text-slate-600 leading-relaxed text-sm">
                    {commodity.description}
                  </p>
                </div>

                {/* Coverage Areas */}
                <div className="p-6 bg-white flex-grow">
                  <div className="text-sm font-semibold text-[#0F2A4D] mb-3">
                    Coverage Areas:
                  </div>
                  <ul className="space-y-2">
                    {commodity.coverage.map((item, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-sm text-slate-600">
                        <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full flex-shrink-0"></div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Button */}
                <div className="px-6 pb-6 bg-white mt-auto">
                  {commodity.anchor ? (
                    <a
                      href="#emissions-detail"
                      className="flex items-center justify-center w-full py-3 bg-[#8B0000] text-white font-medium rounded-md hover:bg-[#6B0000] transition-all duration-200 group/btn"
                    >
                      Explore Emissions Intelligence
                      <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </a>
                  ) : (
                    <Link
                      to="/contact"
                      className="flex items-center justify-center w-full py-3 bg-[#8B0000] text-white font-medium rounded-md hover:bg-[#6B0000] transition-all duration-200 group/btn"
                    >
                      Explore Energia IQ
                      <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Provide Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-6">
                What Our Intelligence Includes
              </h2>
              <div className="space-y-6">
                {[
                  { title: 'Supply & Demand Analysis', desc: 'Detailed modeling of production capacity, consumption trends, and trade flows with quarterly forecasts' },
                  { title: 'Price Forecasts', desc: 'Short, medium, and long-term price outlooks based on fundamental analysis and scenario modeling' },
                  { title: 'Market Structure Analysis', desc: 'Competitive dynamics, market concentration, and strategic positioning of key players' },
                  { title: 'Policy & Regulatory Impact', desc: 'Assessment of government policies, trade regulations, and environmental mandates on commodity markets' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-[#007BFF]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-5 h-5 text-[#007BFF]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#0F2A4D] mb-1">{item.title}</h3>
                      <p className="text-base text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-lg">
              <h3 className="text-xl font-bold text-[#0F2A4D] mb-6">Deliverable Formats</h3>
              <ul className="space-y-4">
                {[
                  'Monthly market reports and outlook updates',
                  'Quarterly deep-dive analytical publications',
                  'Real-time price alerts and market commentary',
                  'Interactive data dashboards and visualization tools',
                  'Custom research briefs on specific topics',
                  'Direct analyst access for Q&A and consultation'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-[#007BFF]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full"></div>
                    </div>
                    <span className="text-base text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Client Base Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-4">
              Trusted by Global Energy Decision-Makers
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-3xl mx-auto">
              Leading organizations worldwide rely on Energia IQ intelligence platforms for strategic decision-making
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {clientTypes.map((client, index) => (
              <div
                key={index}
                className="bg-slate-50 p-6 rounded-xl border border-slate-200 text-center hover:border-[#007BFF]/50 hover:shadow-md transition-all duration-300"
              >
                <div className="w-14 h-14 mx-auto mb-4 bg-white rounded-full flex items-center justify-center text-[#007BFF] shadow-sm border border-slate-100">
                  {client.icon}
                </div>
                <p className="text-[#0F2A4D] font-semibold text-sm">{client.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emissions Tracking Intelligence Section */}
      <section id="emissions-detail" className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F2A4D] mb-4">
              Emissions Tracking & Carbon Markets Intelligence
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-3xl mx-auto">
              Comprehensive intelligence on energy sector emissions, carbon pricing mechanisms, and decarbonization pathways
            </p>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            <div className="text-center bg-white p-6 rounded-xl border border-slate-200">
              <Leaf className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <div className="text-2xl font-bold text-[#0F2A4D] mb-1">50+</div>
              <div className="text-sm text-slate-600">Countries Tracked</div>
            </div>
            <div className="text-center bg-white p-6 rounded-xl border border-slate-200">
              <Factory className="w-8 h-8 text-[#007BFF] mx-auto mb-3" />
              <div className="text-2xl font-bold text-[#0F2A4D] mb-1">1000+</div>
              <div className="text-sm text-slate-600">Facilities Monitored</div>
            </div>
            <div className="text-center bg-white p-6 rounded-xl border border-slate-200">
              <TrendingDown className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <div className="text-2xl font-bold text-[#0F2A4D] mb-1">10+</div>
              <div className="text-sm text-slate-600">Carbon Markets</div>
            </div>
            <div className="text-center bg-white p-6 rounded-xl border border-slate-200">
              <Globe2 className="w-8 h-8 text-[#007BFF] mx-auto mb-3" />
              <div className="text-2xl font-bold text-[#0F2A4D] mb-1">Real-Time</div>
              <div className="text-sm text-slate-600">Data Updates</div>
            </div>
          </div>

          {/* Emissions Services */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {emissionsServices.map((service, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl border border-slate-200 hover:border-[#007BFF] hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-lg font-semibold text-[#0F2A4D] mb-2">
                  {service.title}
                </h3>
                <p className="text-base text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          {/* What We Track */}
          <div className="mb-16">
            <h3 className="text-xl font-bold text-[#0F2A4D] mb-8 text-center">What We Track</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {trackingCapabilities.map((capability, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl border border-slate-200"
                >
                  <h4 className="text-lg font-semibold text-[#0F2A4D] mb-2">
                    {capability.title}
                  </h4>
                  <p className="text-base text-slate-600 leading-relaxed">
                    {capability.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Carbon Markets Coverage */}
          <div className="mb-16">
            <h3 className="text-xl font-bold text-[#0F2A4D] mb-8 text-center">Carbon Markets Coverage</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {carbonMarkets.map((market, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-slate-200 hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-slate-50 to-blue-50 p-5 border-b border-slate-200">
                    <h4 className="text-lg font-bold text-[#0F2A4D] mb-1">
                      {market.name}
                    </h4>
                    <p className="text-sm text-slate-600">{market.description}</p>
                  </div>
                  <div className="p-5">
                    <div className="text-sm font-semibold text-slate-700 mb-2">Coverage Areas:</div>
                    <ul className="space-y-1">
                      {market.coverage.map((item, idx) => (
                        <li key={idx} className="flex items-center space-x-2 text-sm text-slate-600">
                          <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full"></div>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Intelligence Deliverables */}
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <h3 className="text-xl font-bold text-[#0F2A4D] mb-6">Intelligence Deliverables</h3>
              <p className="text-base text-slate-600 mb-6">
                Our emissions tracking service provides regular updates and strategic analysis to support your climate commitments
              </p>
              <ul className="space-y-3">
                {[
                  'Monthly emissions tracking reports by region and sector',
                  'Weekly carbon market commentary and price forecasts',
                  'Quarterly policy and regulatory impact assessments',
                  'Real-time alerts on significant climate policy developments',
                  'Annual decarbonization pathway scenarios and modeling',
                  'Custom emissions analysis and climate strategy consulting'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-1.5 h-1.5 bg-green-700 rounded-full"></div>
                    </div>
                    <span className="text-base text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-green-50 p-6 rounded-xl border border-blue-200 shadow-lg">
              <TrendingDown className="w-10 h-10 text-green-700 mb-4" />
              <h3 className="text-lg font-bold text-[#0F2A4D] mb-3">Net-Zero Transition Intelligence</h3>
              <p className="text-base text-slate-600 mb-4">
                Our proprietary models assess decarbonization pathways and economics including:
              </p>
              <div className="space-y-2">
                {[
                  'Technology adoption curves and cost trajectories',
                  'Carbon abatement cost curves by sector and region',
                  'Investment requirements for net-zero scenarios',
                  'Policy risk assessment and carbon price forecasts',
                  'Stranded asset analysis and transition risks'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-2 p-2 bg-white rounded-md">
                    <div className="w-1.5 h-1.5 bg-green-700 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#157D3F] via-[#195C63] to-[#1D3B87]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Unlock the Intelligence Behind Global Energy Markets
          </h2>
          <p className="text-base lg:text-lg text-blue-100 mb-10 leading-relaxed">
            Access proprietary data, advanced analytics, and expert insights through the Energia IQ platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/energia-iq"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#007BFF] text-white font-semibold rounded-md hover:bg-[#0066DD] transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Explore Platforms
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#0F2A4D] font-semibold rounded-md hover:bg-slate-100 transition-all duration-200 shadow-lg"
            >
              Request a Demonstration
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-semibold rounded-md border-2 border-white/30 hover:bg-white/10 transition-all duration-200"
            >
              Contact Enerlligence
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Commodities;
